import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Package, ChevronRight, Calendar } from 'lucide-react';

export const Orders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-purple-25">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-purple-25 py-12 px-6 lg:px-12">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-light mb-4">My Orders</h1>
          <p className="text-stone-600">View and track your order history</p>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Package className="h-16 w-16 mx-auto mb-4 text-stone-400" />
            <h2 className="text-2xl font-heading font-light mb-2">No Orders Yet</h2>
            <p className="text-stone-600 mb-6">Start shopping to see your orders here</p>
            <Button
              onClick={() => navigate('/shop')}
              className="rounded-full bg-purple-600 hover:bg-purple-700"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-2 bg-purple-100 px-3 py-1 rounded-full">
                        <Package className="h-4 w-4 text-purple-700" />
                        <span className="text-sm font-medium text-purple-900">
                          {order.tracking_number || order.id.slice(0, 8).toUpperCase()}
                        </span>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        order.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : order.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-stone-100 text-stone-800'
                      }`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-stone-500 mb-3">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(order.created_at)}</span>
                    </div>

                    <div className="space-y-2">
                      {order.items && order.items.slice(0, 2).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          {item.image_url && (
                            <img
                              src={item.image_url}
                              alt={item.product_name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="text-sm font-medium">{item.product_name}</p>
                            <p className="text-xs text-stone-500">
                              {item.size} × {item.quantity}
                            </p>
                          </div>
                        </div>
                      ))}
                      {order.items && order.items.length > 2 && (
                        <p className="text-sm text-stone-500">
                          +{order.items.length - 2} more item{order.items.length - 2 > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-2xl font-semibold text-purple-900 mb-2">
                      ${order.total_amount.toFixed(2)}
                    </p>
                    <ChevronRight className="h-5 w-5 text-stone-400 ml-auto" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
